import { useCallback, useMemo, useState } from 'react';
import type { HandOffConversation, HandoffResolvedPeriod, HandoffTab } from '../../types/handoff';
import { useHandoffListQuery, useHandoffResolvedQuery } from '../../hooks/useHandoff';
import { useHandoffRealtime } from '../../hooks/useHandoffRealtime';
import { HandoffQueuePanel } from '../../components/handoff/HandoffQueuePanel';
import { HandoffConversationPanel } from '../../components/handoff/HandoffConversationPanel';
import { HandoffContextPanel } from '../../components/handoff/HandoffContextPanel';
import { NotificationControls } from '../../components/handoff/NotificationControls';
import { AgentStatusToggle } from '../../components/agents/AgentStatusToggle';
import { LiveAgentsToggle } from '../../components/handoff/LiveAgentsToggle';
import { AgentsOnlineRail } from '../../components/handoff/AgentsOnlineRail';

function isFailedMailCase(conversation: HandOffConversation): boolean {
  return conversation.handoffStatus === 'mailed';
}

function matchesTab(conversation: HandOffConversation, tab: HandoffTab): boolean {
  switch (tab) {
    case 'requested':
      return conversation.handoffStatus === 'requested' || isFailedMailCase(conversation);
    case 'mine':
      return conversation.handoffStatus === 'assigned' || conversation.handoffStatus === 'active';
    case 'active':
      return conversation.handoffStatus === 'active';
    case 'resolved':
      return conversation.handoffStatus === 'resolved';
    default:
      return false;
  }
}

export function HandoffPage() {
  const [activeTab, setActiveTab] = useState<HandoffTab>('requested');
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [notificationCount, setNotificationCount] = useState(0);
  const [resolvedPeriod, setResolvedPeriod] = useState<HandoffResolvedPeriod>('today');
  const [mailOnly, setMailOnly] = useState(false);

  const isResolvedTab = activeTab === 'resolved';

  const { data: conversations = [], isLoading, isError, error } = useHandoffListQuery();
  const { data: resolved = [] } = useHandoffResolvedQuery(resolvedPeriod, isResolvedTab);

  const handleNewHandoff = useCallback(() => {
    setNotificationCount((current) => current + 1);
  }, []);

  const handleNewMessage = useCallback(() => {}, []);

  useHandoffRealtime({
    selectedConversationId,
    onNewHandoff: handleNewHandoff,
    onNewMessage: handleNewMessage,
  });

  const displayedConversations = useMemo(() => {
    if (isResolvedTab) return resolved;
    return conversations.filter(
      (conversation) =>
        matchesTab(conversation, activeTab) && (!mailOnly || conversation.isMailHandoff),
    );
  }, [isResolvedTab, resolved, conversations, activeTab, mailOnly]);

  const mailCount = useMemo(
    () => conversations.filter((item) => item.isMailHandoff).length,
    [conversations],
  );

  const selectedConversation = useMemo(() => {
    return (
      [...conversations, ...resolved].find(
        (conversation) => conversation.id === selectedConversationId,
      ) ?? null
    );
  }, [conversations, resolved, selectedConversationId]);

  const counts = useMemo<Record<HandoffTab, number>>(
    () => ({
      requested: conversations.filter(
        (item) => item.handoffStatus === 'requested' || isFailedMailCase(item),
      ).length,
      mine: conversations.filter(
        (item) => item.handoffStatus === 'assigned' || item.handoffStatus === 'active',
      ).length,
      active: conversations.filter((item) => item.handoffStatus === 'active').length,
      resolved: resolved.length,
    }),
    [conversations, resolved],
  );

  if (isLoading) {
    return <div className="p-6 text-sm text-gray-600">Laddar handoff-ärenden...</div>;
  }

  if (isError) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold text-gray-900">Handoff</h1>
        <p className="mt-2 text-sm text-red-600">
          Kunde inte ladda handoff-ärenden.
          {error instanceof Error ? ` ${error.message}` : ''}
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-96px)] flex-col overflow-hidden bg-gray-50">
      <header className="border-b bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-950">Handoff</h1>
            <p className="mt-1 text-sm text-gray-500">
              Hantera chattar där AI:n behöver lämna över till människa.
            </p>
          </div>

          <div className="flex items-center gap-3">
            {notificationCount > 0 && (
              <div className="rounded-full bg-amber-100 px-3 py-1 text-sm font-medium text-amber-800">
                {notificationCount} nya
              </div>
            )}

            <div className="rounded-full border border-gray-200 bg-white px-3 py-1 text-sm text-gray-600">
              {counts.requested} väntar · {counts.active} aktiva
            </div>

            <LiveAgentsToggle />
            <AgentStatusToggle />
            <NotificationControls />
          </div>
        </div>

        <div className="mt-3">
          <AgentsOnlineRail />
        </div>
      </header>

      <main className="grid min-h-0 flex-1 grid-cols-[360px_minmax(0,1fr)_320px]">
        <HandoffQueuePanel
          activeTab={activeTab}
          counts={counts}
          conversations={displayedConversations}
          selectedConversationId={selectedConversationId}
          onSelectConversation={setSelectedConversationId}
          onChangeTab={(tab) => {
            setActiveTab(tab);
            setNotificationCount(0);
          }}
          resolvedPeriod={resolvedPeriod}
          onChangeResolvedPeriod={setResolvedPeriod}
          mailOnly={mailOnly}
          onChangeMailOnly={setMailOnly}
          mailCount={mailCount}
        />

        <HandoffConversationPanel conversation={selectedConversation} />

        <HandoffContextPanel conversation={selectedConversation} />
      </main>
    </div>
  );
}

export default HandoffPage;
export type { HandoffTab };
